import type { GuideCommand } from "@asurion-core/expert-workspace-timeline";
import type { GuideStepSummary } from "@expert/solve-tools";
import type { ReactNode } from "react";

import { Flex } from "@mantine/core";
import { useState } from "react";
import { createPortal } from "react-dom";
import { ErrorBoundary } from "react-error-boundary";

import { InlineGuidedFlowsPanel } from "@/components/workspace/GuidedFlowsPanel";
import { UI_ORCHESTRATION_COMPONENT } from "@/expert-hub/data/expertHubErrorMetric";
import { useSessionId } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { trackedBoundaryProps } from "@/shared-utils";
import { PlatformTimeline } from "@/timeline";

import classes from "./AssistTab.module.css";

/** What AI Assist needs to show the guide inline (`isAiAssistGuidedFlowEnabled`). */
export interface InlineGuide {
    title: string;
    onClose: () => void;
    /** Launches a guide from the Toolbox ("Launch guided flow" / `/g`). */
    toolboxCommand: GuideCommand;
    step?: GuideStepSummary;
}

export interface AssistTabProps {
    guide?: ReactNode;
    isGuideOpen?: boolean;
    /** Set to render the guide inline above the proactive-guidance card; omitted, AI Assist shows no guide. */
    inlineGuide?: InlineGuide;
}

export function AssistTab({ guide, isGuideOpen, inlineGuide }: AssistTabProps) {
    const logger = useLogger({ module: "AssistTab" });
    // Portaled into the timeline's slot rather than passed as a prop so the guide keeps this tree's analytics
    // and feature-flag contexts instead of the timeline's.
    const [guideSlot, setGuideSlot] = useState<HTMLDivElement | null>(null);
    const sessionId = useSessionId();

    return (
        <Flex pos="absolute" inset={0} direction="column" className={classes.root}>
            {inlineGuide && isGuideOpen && guide && guideSlot
                ? createPortal(
                      <InlineGuidedFlowsPanel
                          sessionId={sessionId}
                          guide={guide}
                          title={inlineGuide.title}
                          step={inlineGuide.step}
                          onClose={inlineGuide.onClose}
                      />,
                      guideSlot,
                  )
                : null}
            <ErrorBoundary {...trackedBoundaryProps(logger, UI_ORCHESTRATION_COMPONENT)}>
                <PlatformTimeline guide={inlineGuide?.toolboxCommand} guideSlotRef={setGuideSlot} />
            </ErrorBoundary>
        </Flex>
    );
}
